import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Search, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

/**
 * Reusable multi-select entity picker with **remote debounced search
 * and pagination**. Designed to plug into any KR Hub module — pass in
 * a `fetchPage({ search, page, pageSize })` function that returns
 * `{ items, has_more, total }`. This component never assumes a full
 * directory is loaded in the browser.
 *
 * Contract:
 *   fetchPage: async ({ search, page, pageSize }) =>
 *     { items: Array<{ value, label, sublabel?, meta? }>, has_more: bool, total?: number }
 *   value: Array<{ value, label }>          selected entities
 *   onChange: (Array<{ value, label }>) => void
 *
 * Props:
 *   placeholder, emptyText, pageSize (default 20), debounceMs (default 250),
 *   multi (default true), disabled, testId
 */
export default function EntitySelector({
  fetchPage,
  value = [],
  onChange,
  placeholder = 'Search…',
  emptyText = 'No matches',
  pageSize = 20,
  debounceMs = 250,
  multi = true,
  disabled = false,
  testId = 'entity-selector',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  // Debounce the raw query.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), debounceMs);
    return () => clearTimeout(t);
  }, [query, debounceMs]);

  // Reset page whenever the debounced query changes (or the popover opens fresh).
  useEffect(() => {
    if (!open) return;
    setPage(1);
  }, [debouncedQuery, open]);

  const loadPage = useCallback(async (nextPage) => {
    if (!open) return;
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const res = await fetchPage({ search: debouncedQuery, page: nextPage, pageSize });
      if (reqIdRef.current !== myReqId) return; // outdated response
      setItems((prev) => (nextPage === 1 ? res.items || [] : [...prev, ...(res.items || [])]));
      setHasMore(!!res.has_more);
      setTotal(res.total ?? 0);
    } catch {
      if (reqIdRef.current !== myReqId) return;
      setItems([]);
      setHasMore(false);
      setTotal(0);
    } finally {
      if (reqIdRef.current === myReqId) setLoading(false);
    }
  }, [open, debouncedQuery, fetchPage, pageSize]);

  useEffect(() => { loadPage(page); }, [loadPage, page]);

  const selectedValues = new Set(value.map((v) => v.value));

  const toggle = (item) => {
    if (selectedValues.has(item.value)) {
      onChange?.(value.filter((v) => v.value !== item.value));
    } else if (multi) {
      onChange?.([...value, { value: item.value, label: item.label, sublabel: item.sublabel }]);
    } else {
      onChange?.([{ value: item.value, label: item.label, sublabel: item.sublabel }]);
      setOpen(false);
    }
  };

  const removeChip = (val) => onChange?.(value.filter((v) => v.value !== val));

  return (
    <div className="space-y-2" data-testid={testId}>
      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`${testId}-chips`}>
          {value.map((v) => (
            <Badge
              key={v.value}
              variant="secondary"
              className="text-xs pl-2 pr-1 py-0.5 gap-1"
              data-testid={`${testId}-chip-${v.value}`}
            >
              <span className="truncate max-w-[240px]">{v.label}</span>
              {!disabled && (
                <button
                  type="button"
                  className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                  onClick={() => removeChip(v.value)}
                  data-testid={`${testId}-chip-remove-${v.value}`}
                  aria-label={`Remove ${v.label}`}
                >
                  <X size={12} />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            className="w-full justify-between font-normal"
            disabled={disabled}
            data-testid={`${testId}-trigger`}
          >
            <span className="text-slate-600">
              {value.length === 0
                ? placeholder
                : `${value.length} selected${multi ? '' : ''}`}
            </span>
            <ChevronDown size={14} className="text-slate-400" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <div className="p-2 border-b border-slate-200 flex items-center gap-2">
            <Search size={14} className="text-slate-400 shrink-0" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search…"
              className="h-8 border-0 shadow-none focus-visible:ring-0 px-0"
              data-testid={`${testId}-search-input`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto" data-testid={`${testId}-results`}>
            {loading && page === 1 && (
              <div className="p-3 text-xs text-slate-500 text-center" data-testid={`${testId}-loading`}>
                Searching…
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-3 text-xs text-slate-500 text-center" data-testid={`${testId}-empty`}>
                {emptyText}
              </div>
            )}
            {items.map((item) => {
              const isSelected = selectedValues.has(item.value);
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => toggle(item)}
                  className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-start gap-2 ${
                    isSelected ? 'bg-emerald-50' : ''
                  }`}
                  data-testid={`${testId}-option-${item.value}`}
                >
                  <input
                    type="checkbox"
                    readOnly
                    checked={isSelected}
                    className="mt-0.5 shrink-0"
                    tabIndex={-1}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-900 truncate">{item.label}</div>
                    {item.sublabel && (
                      <div className="text-[11px] text-slate-500 truncate">{item.sublabel}</div>
                    )}
                  </div>
                </button>
              );
            })}
            {hasMore && !loading && (
              <button
                type="button"
                className="w-full py-2 text-xs text-sky-600 hover:bg-slate-50"
                onClick={() => setPage((p) => p + 1)}
                data-testid={`${testId}-load-more`}
              >
                Load more ({total - items.length} remaining)
              </button>
            )}
            {loading && page > 1 && (
              <div className="p-2 text-xs text-slate-500 text-center">Loading more…</div>
            )}
          </div>
          {total > 0 && (
            <div className="border-t border-slate-200 px-3 py-1.5 text-[11px] text-slate-500 flex items-center justify-between">
              <span>{items.length} of {total}</span>
              <span>{value.length} selected</span>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
